import { TaskItem, TaskItemAttachments as TaskItemAttachment, TaskObject, WorkManagerProject, TaskObjectModel } from "@medapro-types/WorkManager/WorkManager";
import { getHexFromAhex } from "@util/color";
import api from "./api";
import endpoints from "./endpoints";
import docExtensions from "@/plugins/document-extensions";

export default {
  getUserTasks: async (taskType: "MyTasks" | "AllTasks" | "DelegatedTask"): Promise<TaskItem[]> => {
    const response = await api.get(endpoints.userTasks.get, { params: { taskType } });
    return response.data;
  },
  getUserTaskDetails: async (taskId: string): Promise<TaskItem | null> => {
    const response = await api.get(endpoints.userTasks.getUserTaskDetails, { params: { taskId } });
    return response.data;
  },
  getUserTasksForCurrentMonth: async () => {
    const response = await api.get(endpoints.userTasks.getCurrentMonth);
    return response.data;
  },
  createUserTask(newTaskName: string) {
    return api.post(endpoints.userTasks.create, {}, { params: { newTaskName } }).then((x) => Promise.resolve(x.data));
  },
  createWorkManagerUserTask(newTaskName: string) {
    return api.post(endpoints.userTasks.createWorkManagerUserTask, {}, { params: { newTaskName } }).then((x) => Promise.resolve(x.data));
  },
  createUserTaskFromAdvancedSearch: async (advancedSearchId: string) => {
    const response = await api.post(endpoints.userTasks.createFromAdvancedSearch, {}, { params: { advancedSearchId } });
    return response.data;
  },
  deleteUserTask(userTaskIds: string[]) {
    const response = api.post(endpoints.userTasks.delete, userTaskIds);
    return response;
  },
  updateUserTask(model: TaskItem) {
    return api.post(endpoints.userTasks.update, model, {}).then((x) => Promise.resolve(x.data));
  },
  updateUserTaskDetails(model: TaskItem) {
    return api.post(endpoints.userTasks.updateUserTaskDetails, model, {}).then((x) => Promise.resolve(x.data));
  },
  getProjects(): Promise<WorkManagerProject[]> {
    return api.get(endpoints.userTasks.getProjects).then((x) => Promise.resolve(x.data));
  },
  async createOrUpdateProjects(projects: WorkManagerProject[]): Promise<unknown[]> {
    const response = await api.post(endpoints.userTasks.updateProjects, projects);
    return response.data;
  },
  async deleteProjects(ids: string[]): Promise<string> {
    const response = await api.post(endpoints.userTasks.deleteProjects, ids);
    return response.data;
  },
  async getTaskAttachments(taskId: string): Promise<TaskItemAttachment[]> {
    const response = await api.get(endpoints.userTasks.getAttachmentDetails, { params: { taskId } });
    return response.data;
  },
  async addTaskAttachment(taskId: string, file: File, comments: string = ""): Promise<TaskItemAttachment> {
    const response = await api.post(
      endpoints.userTasks.addAttachment,
      { file },
      {
        params: { taskId, comments },
        headers: { "Content-Type": "multipart/form-data" }
      }
    );
    return response.data;
  },
  async updateAttachments(taskId: string, attachments: TaskItemAttachment[]): Promise<TaskItemAttachment[]> {
    const response = await api.post(endpoints.userTasks.updateAttachment, attachments, { params: { taskId } });
    return response.data;
  },
  getTaskArticles: async (taskId: string, languageCode: string, signal: AbortSignal): Promise<TaskObject[]> => {
    const response = await api.get(endpoints.userTasks.getTaskObjects, { params: { taskId, languageCode }, signal });
    const data: TaskObject[] = response.data;
    for (const article of data) {
      "color" in article ? (article.color = getHexFromAhex(article.color)) : null;
    }
    return data;
  },
  async sendMail(toAddresses: string[], taskId: string): Promise<unknown> {
    const frontendUrl = docExtensions.getFrontendUrl().replace(/\/$/, "");
    const response = await api.post(endpoints.userTasks.sendMail, toAddresses, { params: { taskId, frontEndUrl: frontendUrl } });
    return response.data;
  },
  getArticleStateAndVariationGroup: async function (articleId: string, language: string): Promise<any> {
    const response = await api.get(endpoints.article.getArticleStateAndVariationGroup, { params: { articleId, language } });
    return response.data;
  },
  updateObjectDetails(taskId: string, languageCode: string, objectType: number, model: TaskObjectModel[]) {
    return api.post(endpoints.userTasks.updateObjectDetails, model, { params: { taskId, languageCode, objectType } }).then((x) => Promise.resolve(x.data));
  }
};
